import React, { useState, useMemo, useEffect } from 'react';
import { X, Equals } from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import {
  TransactionWithPayments,
  TruckAttributionInput,
} from '../../types/apiTypes';
import { Truck } from '../../types/dbTypes';
import AllocationChart from './AllocationChart';
import {
  splitEvenlyCents,
  validateAllocations,
  calculateUnattributedCents,
  formatCentsToDollars,
  parseDollarsToCents,
  getTruckColor,
  UNATTRIBUTED_COLOR,
  NULL_UUID,
} from '../../utils/attributionUtils';

interface SplitAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionWithPayments;
  selectedTrucks: Truck[];
  onSave: (attributions: TruckAttributionInput[]) => Promise<void>;
  isSaving?: boolean;
}

type InputMode = 'dollar' | 'percentage';

interface AllocationRow {
  truck_id: string;
  truck_display_name: string;
  amount_cents: number;
  percentage: number;
  color: string;
}

const SplitAllocationModal: React.FC<SplitAllocationModalProps> = ({
  isOpen,
  onClose,
  transaction,
  selectedTrucks,
  onSave,
  isSaving = false,
}) => {
  // transaction.amount is in dollars from the API, convert to cents for calculations
  const totalCents = Math.abs(Math.round((transaction.amount || 0) * 100));
  const isExpense = (transaction.amount || 0) < 0;

  const [inputMode, setInputMode] = useState<InputMode>('dollar');
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize allocations when trucks or transaction changes
  useEffect(() => {
    if (selectedTrucks.length > 0) {
      // Check if there are existing attributions to pre-populate
      const existingAttributions = (transaction.truckAttributions || []).filter(
        (attr) => attr.truck_id !== NULL_UUID
      ); // Exclude unattributed amounts

      // Check if the selected trucks exactly match the existing attributions
      const selectedTruckIds = new Set(selectedTrucks.map((t) => t.truck_id));
      const existingTruckIds = new Set(
        existingAttributions.map((a) => a.truck_id)
      );

      // Only pre-fill if the sets are identical (same trucks, no additions/removals)
      const trucksMatch =
        selectedTruckIds.size === existingTruckIds.size &&
        Array.from(selectedTruckIds).every((id) => existingTruckIds.has(id));

      const initialAllocations = selectedTrucks.map((truck, index) => {
        // Only use existing attribution if trucks haven't changed
        const existingAttr = trucksMatch
          ? existingAttributions.find((a) => a.truck_id === truck.truck_id)
          : undefined;

        // existingAttr.amount is in dollars from the API, convert to cents
        const amountCents = existingAttr
          ? Math.round((existingAttr.amount ?? 0) * 100)
          : 0;
        const percentage =
          totalCents > 0 ? (amountCents / totalCents) * 100 : 0;

        return {
          truck_id: truck.truck_id,
          truck_display_name: truck.truck_display_name || `Truck ${index + 1}`,
          amount_cents: amountCents,
          percentage: Math.round(percentage * 100) / 100,
          color: getTruckColor(index),
        };
      });
      setAllocations(initialAllocations);
      setError(null);
    }
  }, [selectedTrucks, transaction, totalCents]);

  // Calculate totals
  const allocatedCents = useMemo(() => {
    return allocations.reduce((sum, a) => sum + a.amount_cents, 0);
  }, [allocations]);

  const allocatedPercentage = useMemo(() => {
    return totalCents > 0 ? (allocatedCents / totalCents) * 100 : 0;
  }, [allocatedCents, totalCents]);

  const unattributedCents = useMemo(() => {
    return calculateUnattributedCents(
      allocations.map((a) => a.amount_cents),
      totalCents
    );
  }, [allocations, totalCents]);

  // Build chart segments
  const chartSegments = useMemo(() => {
    return allocations
      .filter((a) => a.amount_cents > 0)
      .map((a) => ({
        truck_id: a.truck_id,
        truck_display_name: a.truck_display_name,
        amount_cents: a.amount_cents,
        color: a.color,
      }));
  }, [allocations]);

  // Validate allocations whenever they change
  useEffect(() => {
    const amounts = allocations.map((a) => a.amount_cents);
    const validation = validateAllocations(amounts, totalCents);
    setError(validation.error);
  }, [allocations, totalCents]);

  const handleDollarChange = (truckId: string, dollarValue: string) => {
    const cents = parseDollarsToCents(dollarValue);
    setAllocations((prev) => {
      return prev.map((a) => {
        if (a.truck_id === truckId) {
          const percentage = totalCents > 0 ? (cents / totalCents) * 100 : 0;
          return {
            ...a,
            amount_cents: cents,
            percentage: Math.round(percentage * 100) / 100,
          };
        }
        return a;
      });
    });
  };

  const handlePercentageChange = (truckId: string, percentValue: string) => {
    const percentage = parseFloat(percentValue) || 0;
    const cents = Math.round((percentage / 100) * totalCents);
    setAllocations((prev) => {
      return prev.map((a) => {
        if (a.truck_id === truckId) {
          return { ...a, amount_cents: cents, percentage };
        }
        return a;
      });
    });
  };

  const handleClear = () => {
    setAllocations((prev) =>
      prev.map((a) => ({
        ...a,
        amount_cents: 0,
        percentage: 0,
      }))
    );
    setError(null);
  };

  const handleSplitEvenly = () => {
    const amounts = splitEvenlyCents(totalCents, allocations.length);
    setAllocations((prev) =>
      prev.map((a, i) => ({
        ...a,
        amount_cents: amounts[i],
        percentage: totalCents > 0 ? (amounts[i] / totalCents) * 100 : 0,
      }))
    );
  };

  const handleSave = async () => {
    if (error) return;

    const attributions: TruckAttributionInput[] = allocations
      .filter((a) => a.amount_cents > 0)
      .map((a) => ({
        truck_id: a.truck_id,
        amount: a.amount_cents,
      }));

    // Add unattributed portion if exists
    if (unattributedCents > 0) {
      attributions.push({
        truck_id: NULL_UUID,
        amount: unattributedCents,
      });
    }

    // onSave rejects on failure (callers toast the error themselves); keep
    // the modal open so the user can retry, and don't let the rejection
    // escape this un-awaited click handler as an unhandled rejection.
    try {
      await onSave(attributions);
      onClose();
    } catch {
      // Feedback already shown by onSave; modal stays open for retry.
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#0D0F1C]/60 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-[480px] overflow-hidden rounded-[8px] bg-white shadow-2xl">
        {/* Header */}
        <div className="relative border-b border-[#F0F1F3] bg-gradient-to-b from-white to-[#FAFBFC] px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#9CA3AF] transition-all hover:bg-[#F3F4F6] hover:text-[#6B7280]"
          >
            <X size={18} weight="bold" />
          </button>

          <div className="pr-8">
            <h2 className="text-[18px] font-semibold tracking-[-0.01em] text-primary">
              Allocate Transaction
            </h2>
            <p className="mt-1 text-[13px] text-[#6B7280]">
              Distribute this expense across your trucks
            </p>
          </div>

          {/* Transaction Amount Card */}
          <div className="mt-5 flex items-center justify-between rounded-[6px] border border-[#E5E7EB] bg-white px-4 py-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-[#9CA3AF]">
                Transaction Total
              </p>
              <p className="mt-0.5 text-[22px] font-semibold tabular-nums tracking-[-0.02em] text-primary">
                {formatCentsToDollars(totalCents)}
              </p>
            </div>
            <div
              className={`rounded-full px-3 py-1.5 text-[12px] font-medium ${
                isExpense
                  ? 'bg-[#FEF2F2] text-[#DC2626]'
                  : 'bg-[#F0FDF4] text-[#16A34A]'
              }`}
            >
              {isExpense ? 'Expense' : 'Income'}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[calc(90vh-280px)] overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {/* Mode Toggle */}
          <div className="mb-5">
            <div className="inline-flex rounded-[6px] border border-[#E5E7EB] bg-[#F9FAFB] p-1">
              <button
                type="button"
                onClick={() => setInputMode('dollar')}
                className={`rounded-[4px] px-4 py-2 text-[13px] font-medium transition-all ${
                  inputMode === 'dollar'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151]'
                }`}
              >
                Dollar Amount
              </button>
              <button
                type="button"
                onClick={() => setInputMode('percentage')}
                className={`rounded-[4px] px-4 py-2 text-[13px] font-medium transition-all ${
                  inputMode === 'percentage'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-[#6B7280] hover:text-[#374151]'
                }`}
              >
                Percentage
              </button>
            </div>
          </div>

          {/* Allocation Visualization */}
          <div className="mb-5 rounded-[6px] border border-[#E5E7EB] bg-[#FAFBFC] p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[12px] font-medium text-[#6B7280]">
                Allocation Preview
              </span>
              <span className="text-[12px] font-semibold tabular-nums text-primary">
                {allocatedPercentage.toFixed(1)}% allocated
              </span>
            </div>
            <AllocationChart
              allocations={chartSegments}
              unattributedCents={unattributedCents}
              totalCents={totalCents}
              height={12}
            />
          </div>

          {/* Allocation Rows */}
          <div className="space-y-2">
            {allocations.map((allocation, index) => (
              <div
                key={allocation.truck_id}
                className="group flex items-center gap-3 rounded-[6px] border border-[#E5E7EB] bg-white p-3 transition-all hover:border-[#D1D5DB] hover:shadow-sm"
              >
                {/* Color indicator & truck name */}
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    className="h-3 w-3 flex-shrink-0 rounded-full ring-2 ring-white"
                    style={{ backgroundColor: allocation.color }}
                  />
                  <span className="truncate text-[14px] font-medium text-primary">
                    {allocation.truck_display_name}
                  </span>
                </div>

                {/* Input field */}
                <div className="w-[130px]">
                  {inputMode === 'dollar' ? (
                    <div className="flex items-center overflow-hidden rounded-[4px] border border-[#E5E7EB] bg-[#F9FAFB] transition-all focus-within:border-primary focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/10">
                      <span className="pl-3 text-[14px] text-[#9CA3AF]">$</span>
                      <input
                        type="text"
                        value={
                          allocation.amount_cents > 0
                            ? (allocation.amount_cents / 100).toFixed(2)
                            : ''
                        }
                        onChange={(e) =>
                          handleDollarChange(
                            allocation.truck_id,
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                        className="w-full border-none bg-transparent py-2 pr-3 text-right text-[14px] font-medium tabular-nums text-primary outline-none placeholder:text-[#D1D5DB]"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center overflow-hidden rounded-[4px] border border-[#E5E7EB] bg-[#F9FAFB] transition-all focus-within:border-primary focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/10">
                      <input
                        type="text"
                        value={
                          allocation.percentage > 0
                            ? allocation.percentage.toFixed(2)
                            : ''
                        }
                        onChange={(e) =>
                          handlePercentageChange(
                            allocation.truck_id,
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                        className="w-full border-none bg-transparent py-2 pl-3 text-right text-[14px] font-medium tabular-nums text-primary outline-none placeholder:text-[#D1D5DB]"
                      />
                      <span className="pr-3 text-[14px] text-[#9CA3AF]">%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Unattributed Row */}
            {unattributedCents > 0 && (
              <div className="flex items-center gap-3 rounded-[6px] border border-dashed border-[#E5E7EB] bg-[#FAFBFC] p-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    className="h-3 w-3 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: UNATTRIBUTED_COLOR }}
                  />
                  <span className="text-[14px] text-[#9CA3AF]">
                    Business
                  </span>
                </div>
                <div className="w-[130px] text-right">
                  <span className="text-[14px] font-medium tabular-nums text-[#9CA3AF]">
                    {inputMode === 'dollar'
                      ? formatCentsToDollars(unattributedCents)
                      : `${((unattributedCents / totalCents) * 100).toFixed(2)}%`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-[6px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#DC2626]">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-1.5 border-t border-[#F0F1F3] bg-[#FAFBFC] px-2 py-2 sm:gap-2 sm:px-6 sm:py-4">
          <div className="flex gap-1 sm:gap-2">
            <button
              type="button"
              onClick={handleClear}
              disabled={isSaving}
              className="rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium text-[#6B7280] transition-all hover:bg-[#F3F4F6] hover:text-[#374151] disabled:opacity-50 sm:rounded-[6px] sm:px-4 sm:py-2.5 sm:text-[13px]"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleSplitEvenly}
              disabled={isSaving}
              className="flex items-center gap-0.5 rounded-[3px] border border-[#E5E7EB] bg-white px-1.5 py-0.5 text-[10px] font-medium text-primary shadow-sm transition-all hover:bg-[#F9FAFB] disabled:opacity-50 sm:gap-1.5 sm:rounded-[6px] sm:px-4 sm:py-2.5 sm:text-[13px]"
            >
              <Equals size={10} weight="bold" className="sm:hidden" />
              <Equals size={14} weight="bold" className="hidden sm:block" />
              <span className="hidden sm:inline">Split Evenly</span>
              <span className="sm:hidden">Split</span>
            </button>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              isSaving ||
              !!error ||
              allocations.every((a) => a.amount_cents === 0)
            }
            className="flex min-w-[50px] items-center justify-center gap-0.5 rounded-[3px] bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm transition-all hover:bg-[#2A2F4F] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[100px] sm:gap-2 sm:rounded-[6px] sm:px-3 sm:py-2.5 sm:text-[13px]"
          >
            {isSaving ? (
              <>
                <ClipLoader size={10} color="#ffffff" className="" />
              </>
            ) : (
              <>
                <span className="">Save</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitAllocationModal;
